import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Button, TextField, Divider, Grid, Snackbar, Alert, CircularProgress } from "@mui/material";
import { labelStyles, formFieldStyles } from "../../styles/formStyles";
import { updateUserProfile, getUserProfile } from "../../services/UserProfile";
import { IUpdateUserDTO, IUserProfile } from "../../interfaces/UserProfile";
import { useAuth } from "../../auth/AuthProvider";

interface EditProfileProps {
  onCancel: () => void;
  onSave: (message: string) => void;
}

const EditProfile = ({ onCancel, onSave }: EditProfileProps) => {
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const userId = loggedInUser?.id;
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: ""
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const profile: IUserProfile = await getUserProfile();
      if (profile) {
        setFormData({
          firstName: profile.firstName || "",
          lastName: profile.lastName || ""
        });
        setImagePreview(profile.profileImgUrl || "");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      const userName = localStorage.getItem("userName") || "";
      const nameParts = userName.split(" ");
      setFormData({
        firstName: nameParts[0] || "",
        lastName: nameParts[1] || ""
      });
      setImagePreview("");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbarMessage('Only JPG, JPEG, and PNG files are allowed');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbarMessage('File size must be less than 5MB');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
      return;
    }

    setProfileImage(file);
    setImagePreview(URL.createObjectURL(file));
    setHasChanges(true);
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview("");
    setHasChanges(true);
  };

  const validate = () => {
    const newErrors: any = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    if (!userId) {
      setSnackbarMessage('User ID not available');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
      return;
    }

    try {
      const dto: IUpdateUserDTO = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      };

      const response = await updateUserProfile(userId, dto, profileImage || undefined);

      // Handle response based on the actual API response structure
      if (response && (response.status === 'success' || response.success)) {
        localStorage.setItem("userName", `${dto.firstName} ${dto.lastName}`);
        onSave(response.message || "Profile updated successfully");
      } else {
        throw new Error(response?.message || "Failed to update profile");
      }
    } catch (err: any) {
      console.error("Update profile error:", err);
      
      let errorMessage = "An error occurred while updating profile";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: 'calc(100vh - 3.5rem)', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 3.5rem)', p: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'white' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>Profile</Typography>
          <Typography variant="body2" color="grey.600">Delivery Partner • Main Warehouse</Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography component="label" sx={labelStyles}>
                First Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({ ...formData, firstName: e.target.value });
                  setErrors({ ...errors, firstName: "" });
                  setHasChanges(true);
                }}
                error={!!errors.firstName}
                helperText={errors.firstName}
                sx={formFieldStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography component="label" sx={labelStyles}>
                Last Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData({ ...formData, lastName: e.target.value });
                  setErrors({ ...errors, lastName: "" });
                  setHasChanges(true);
                }}
                error={!!errors.lastName}
                helperText={errors.lastName}
                sx={formFieldStyles}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={600} mb={0.5}>Display Picture</Typography>
          <Typography variant="caption" color="grey.600" mb={2} display="block">Assign a avatar for your profile</Typography>
          
          <Avatar sx={{ width: 96, height: 96, mb: 2, bgcolor: "#61708A" }}>
            {imagePreview ? (
              <img 
                src={imagePreview} 
                alt="Profile" 
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} 
              />
            ) : (
              <img 
                src="/sidebar_logo.png" 
                alt="Default Profile" 
                style={{ width: '60%', height: '60%', objectFit: 'contain' }} 
              />
            )}
          </Avatar>
          
          <Typography variant="body2" fontWeight={600} mb={1}>Note</Typography>
          <Typography variant="caption" color="grey.600" component="ul" pl={3} mb={2}>
            <li>JPG, JPEG, and PNG are allowed</li>
            <li>Maximum upload file size ~ 5MB</li>
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 4 }}>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              style={{ display: 'none' }}
              id="image-upload"
              onChange={handleImageUpload}
            />
            <label htmlFor="image-upload">
              <Button 
                component="span"
                variant="outlined" 
                sx={{
                  borderRadius: "1.75rem",
                  border: "1px solid",
                  borderColor: "grey.100",
                  color: "primary.main",
                  textTransform: "none",
                  px: "1rem",
                  py: "0.125rem",
                  "&:hover": { borderColor: "primary.main" }
                }}
              >
                Upload Image
              </Button>
            </label>
            {imagePreview && (
              <Button 
                onClick={handleRemoveImage}
                variant="outlined" 
                sx={{
                  borderRadius: "1.75rem",
                  border: "1px solid",
                  borderColor: "error.light",
                  color: "error.main",
                  textTransform: "none",
                  px: "1rem",
                  py: "0.125rem",
                  "&:hover": { borderColor: "error.main" }
                }}
              >
                Remove
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2, pt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="outlined" 
              onClick={onCancel}
              sx={{
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
                boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
                padding: "8px 16px",
                textTransform: "none"
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
                boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
                padding: "8px 40px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "primary.main",
                  boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset"
                }
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>
      
      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditProfile;
